import { useState, useEffect } from 'react'
import { Card, Button, Input, Modal, Table, Loading, Badge, Alert, Select } from '../components/common'
import { masterService, bookService, schoolService } from '../services/masterService'
import { Trash2, BookOpen, Plus, X } from 'lucide-react'

function SchoolsPage() {
  const [schools, setSchools] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingSchool, setEditingSchool] = useState(null)
  const [search, setSearch] = useState('')
  const [formData, setFormData] = useState({ 
    code: '', name: '', network: '', jenjang: 'SMP', 
    address: '', city: '', province: '', phone: '', pic: '' 
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState(null) // { id, name }
  const [bookNeedsModal, setBookNeedsModal] = useState(null) // school object
  const [bookNeedsItems, setBookNeedsItems] = useState([]) // [{ book_id, qty }]
  const [booksList, setBooksList] = useState([])
  const [savingBookNeeds, setSavingBookNeeds] = useState(false)
  const [loadingBookNeeds, setLoadingBookNeeds] = useState(false)
  const [bookNeedsError, setBookNeedsError] = useState('')

  useEffect(() => { fetchSchools() }, [])

  const fetchSchools = async () => {
    try {
      setLoading(true)
      const response = await masterService.getSchools({ search })
      if (response.success) setSchools(response.data.schools || [])
    } catch (err) { console.error(err) }
    finally { setLoading(false) }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.code || !formData.name || !formData.jenjang) {
      setError('Kode, nama, dan jenjang harus diisi')
      return
    }
    setSaving(true)
    setError('')
    try {
      const response = editingSchool
        ? await masterService.updateSchool(editingSchool.id, formData)
        : await masterService.createSchool(formData)
      if (response.success) {
        setShowModal(false)
        setEditingSchool(null)
        resetForm()
        fetchSchools()
      } else {
        setError(response.message || 'Gagal menyimpan')
      }
    } catch (err) { setError(err.response?.data?.message || 'Gagal menyimpan') }
    finally { setSaving(false) }
  }

  const resetForm = () => {
    setFormData({ 
      code: '', name: '', network: '', jenjang: 'SMP', 
      address: '', city: '', province: '', phone: '', pic: '' 
    })
  }

  const handleEdit = (school) => {
    setEditingSchool(school)
    setFormData({ 
      code: school.code || '', 
      name: school.name || '', 
      network: school.network || '',
      jenjang: school.jenjang || 'SMP',
      address: school.address || '', 
      city: school.city || '', 
      province: school.province || '',
      phone: school.phone || '',
      pic: school.pic || ''
    })
    setError('')
    setShowModal(true)
  }

  const handleDeleteClick = (school) => {
    setDeleteConfirm({ id: school.id, name: school.name, code: school.code })
  }

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return
    try {
      await masterService.deleteSchool(deleteConfirm.id)
      setDeleteConfirm(null)
      fetchSchools()
    } catch (err) {
      alert(err.response?.data?.message || 'Gagal menghapus')
      setDeleteConfirm(null)
    }
  }

  const openAddModal = () => {
    setEditingSchool(null)
    resetForm()
    setError('')
    setShowModal(true)
  }

  const openBookNeedsModal = async (school) => {
    setBookNeedsModal(school)
    setBookNeedsError('')
    setLoadingBookNeeds(true)
    try {
      const booksRes = await bookService.getAll({ status: 'active' })
      setBooksList(booksRes.data?.books || [])

      try {
        const needsRes = await schoolService.getBookNeeds(school.id)
        setBookNeedsItems(
          (needsRes.data?.items || []).map(item => ({ book_id: item.book_id, qty: item.qty }))
        )
      } catch (err) {
        console.error('Error fetching book needs:', err)
        setBookNeedsItems([])
      }
    } catch (err) {
      setBookNeedsError('Gagal memuat data buku')
      console.error(err)
    } finally {
      setLoadingBookNeeds(false)
    }
  }

  const handleAddBookNeedRow = () => {
    setBookNeedsItems(prev => [...prev, { book_id: '', qty: 0 }])
  }

  const handleRemoveBookNeedRow = (index) => {
    setBookNeedsItems(prev => prev.filter((_, i) => i !== index))
  }

  const handleBookNeedChange = (index, field, value) => {
    setBookNeedsItems(prev => prev.map((item, i) => {
      if (i !== index) return item
      if (field === 'qty') return { ...item, qty: parseInt(value) || 0 }
      if (field === 'book_id') return { ...item, book_id: value ? parseInt(value) : '' }
      return { ...item, [field]: value }
    }))
  }

  const handleSaveBookNeeds = async () => {
    const validItems = bookNeedsItems.filter(item => item.book_id && item.qty > 0)
    const bookIds = validItems.map(i => i.book_id)
    if (new Set(bookIds).size !== bookIds.length) {
      setBookNeedsError('Tidak boleh ada buku yang duplikat')
      return
    }
    setSavingBookNeeds(true)
    setBookNeedsError('')
    try {
      const res = await schoolService.saveBookNeeds(bookNeedsModal.id, validItems)
      if (res.success) {
        setBookNeedsModal(null)
      } else {
        setBookNeedsError(res.message || 'Gagal menyimpan')
      }
    } catch (err) {
      setBookNeedsError(err.response?.data?.message || 'Gagal menyimpan')
    } finally {
      setSavingBookNeeds(false)
    }
  }

  const availableBookOptions = (currentBookId) => {
    const usedIds = bookNeedsItems.map(i => i.book_id).filter(id => id && id !== currentBookId)
    return booksList.filter(b => !usedIds.includes(b.id))
  }

  const totalBookNeeds = bookNeedsItems.reduce((sum, item) => sum + (item.qty || 0), 0)

  const getJenjangBadge = (jenjang) => {
    const styles = {
      SD: 'bg-green-100 text-green-700',
      SMP: 'bg-blue-100 text-blue-700',
      SMA: 'bg-purple-100 text-purple-700',
      SMK: 'bg-orange-100 text-orange-700',
    }
    return styles[jenjang] || 'bg-gray-100 text-gray-700'
  }

  const columns = [
    { header: 'Kode', render: (row) => <span className="font-mono font-medium text-sm">{row.code}</span> },
    { header: 'Nama', render: (row) => <span className="font-medium">{row.name}</span> },
    { header: 'Jenjang', render: (row) => <span className={`px-2 py-1 rounded text-xs font-medium ${getJenjangBadge(row.jenjang)}`}>{row.jenjang}</span> },
    { header: 'Kota', render: (row) => row.city || '-' },
    { header: 'Network', render: (row) => row.network || '-' },
    { header: 'Status', render: (row) => <Badge variant={row.status === 'active' ? 'success' : 'default'}>{row.status}</Badge> },
    {
      header: 'Aksi', render: (row) => (
        <div className="flex gap-2">
          <button onClick={() => openBookNeedsModal(row)} className="text-emerald-600 hover:underline text-sm flex items-center gap-1">
            <BookOpen className="w-3.5 h-3.5" />Kebutuhan
          </button>
          <button onClick={() => handleEdit(row)} className="text-blue-600 hover:underline text-sm">Edit</button>
          <button onClick={() => handleDeleteClick(row)} className="text-red-600 hover:underline text-sm">Hapus</button>
        </div>
      )
    }
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">🏫 Master Sekolah</h1>
          <p className="text-gray-500">Kelola data sekolah pelanggan</p>
        </div>
        <Button onClick={openAddModal}>
          ➕ Tambah Sekolah
        </Button>
      </div>

      <Card>
        <div className="flex gap-4 mb-4">
          <Input 
            placeholder="Cari sekolah..." 
            value={search} 
            onChange={(e) => setSearch(e.target.value)} 
            className="w-64" 
          />
          <Button variant="secondary" onClick={fetchSchools}>🔍 Cari</Button>
        </div>
        {loading ? <Loading /> : <Table columns={columns} data={schools} emptyText="Belum ada data sekolah" />}
      </Card>

      <Modal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)} 
        title={editingSchool ? 'Edit Sekolah' : 'Tambah Sekolah'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Batal</Button>
            <Button onClick={handleSubmit} loading={saving}>Simpan</Button>
          </>
        }
      >
        {error && <Alert type="error" className="mb-4">{error}</Alert>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input 
              label="Kode Sekolah *" 
              value={formData.code} 
              onChange={(e) => setFormData(p => ({ ...p, code: e.target.value.toUpperCase() }))} 
              disabled={!!editingSchool}
              placeholder="ABH-CRB"
            />
            <Select 
              label="Jenjang *" 
              value={formData.jenjang} 
              onChange={(e) => setFormData(p => ({ ...p, jenjang: e.target.value }))}
              options={[
                { value: 'SD', label: 'SD' },
                { value: 'SMP', label: 'SMP' },
                { value: 'SMA', label: 'SMA' },
                { value: 'SMK', label: 'SMK' },
              ]}
            />
          </div>
          <Input 
            label="Nama Sekolah *" 
            value={formData.name} 
            onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} 
            placeholder="SMP Al-Bahjah Cirebon"
          />
          <Select 
            label="Network/Jaringan" 
            value={formData.network} 
            onChange={(e) => setFormData(p => ({ ...p, network: e.target.value }))}
            options={[
              { value: '', label: '-- Pilih Network --' },
              { value: 'JSIT', label: 'JSIT' },
              { value: 'Al-Bahjah', label: 'Al-Bahjah' },
              { value: 'Hidayatullah', label: 'Hidayatullah' },
              { value: 'Muhammadiyah', label: 'Muhammadiyah' },
              { value: 'NU', label: 'NU' },
              { value: 'Independen', label: 'Independen' },
            ]}
          />
          <Input 
            label="Alamat" 
            value={formData.address} 
            onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))} 
            placeholder="Jl. Pendidikan No. 123"
          />
          <div className="grid grid-cols-2 gap-4">
            <Input 
              label="Kota" 
              value={formData.city} 
              onChange={(e) => setFormData(p => ({ ...p, city: e.target.value }))} 
              placeholder="Cirebon"
            />
            <Input 
              label="Provinsi" 
              value={formData.province} 
              onChange={(e) => setFormData(p => ({ ...p, province: e.target.value }))} 
              placeholder="Jawa Barat"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input 
              label="Telepon" 
              value={formData.phone} 
              onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))} 
              placeholder="0231-12345678"
            />
            <Input 
              label="PIC" 
              value={formData.pic} 
              onChange={(e) => setFormData(p => ({ ...p, pic: e.target.value }))} 
              placeholder="Ustadz Ahmad"
            />
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-2xl">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Hapus Sekolah?</h3>
              <p className="text-gray-600 mb-1">
                Anda yakin ingin menghapus sekolah:
              </p>
              <p className="font-medium text-gray-900 mb-4">
                {deleteConfirm.code} - {deleteConfirm.name}
              </p>
              <p className="text-sm text-red-600 mb-6">
                Tindakan ini tidak dapat dibatalkan.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 py-2 border rounded-lg text-gray-700 hover:bg-gray-100 font-medium"
                >
                  Batal
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                >
                  Ya, Hapus
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Book Needs Modal */}
      <Modal
        isOpen={!!bookNeedsModal}
        onClose={() => setBookNeedsModal(null)}
        title={`Kebutuhan Buku — ${bookNeedsModal?.name || ''}`}
        size="lg"
        footer={
          <>
            <div className="flex-1 text-left text-sm text-gray-600">
              Total: <span className="font-bold text-gray-900">{totalBookNeeds.toLocaleString()}</span> buku
            </div>
            <Button variant="secondary" onClick={() => setBookNeedsModal(null)}>Batal</Button>
            <Button onClick={handleSaveBookNeeds} loading={savingBookNeeds}>Simpan</Button>
          </>
        }
      >
        {bookNeedsError && <Alert type="error" className="mb-4">{bookNeedsError}</Alert>}
        {loadingBookNeeds ? <Loading text="Memuat data..." /> : (
          <div className="space-y-3">
            {bookNeedsItems.length === 0 && (
              <p className="text-gray-400 text-center py-4">Belum ada data kebutuhan. Klik tombol di bawah untuk menambah.</p>
            )}
            {bookNeedsItems.map((item, index) => (
              <div key={index} className="flex items-end gap-3">
                <div className="flex-1">
                  {index === 0 && <label className="block text-sm font-medium text-gray-700 mb-1">Buku</label>}
                  <select
                    value={item.book_id}
                    onChange={(e) => handleBookNeedChange(index, 'book_id', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">-- Pilih Buku --</option>
                    {availableBookOptions(item.book_id).map(b => (
                      <option key={b.id} value={b.id}>
                        [{b.kode}] {b.judul} {b.mapel ? `- ${b.mapel}` : ''} {b.kelas ? `Kls ${b.kelas}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="w-28">
                  {index === 0 && <label className="block text-sm font-medium text-gray-700 mb-1">Jumlah</label>}
                  <input
                    type="number"
                    min="0"
                    value={item.qty || ''}
                    onChange={(e) => handleBookNeedChange(index, 'qty', e.target.value)}
                    placeholder="0"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveBookNeedRow(index)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  title="Hapus baris"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddBookNeedRow}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium mt-2"
            >
              <Plus className="w-4 h-4" /> Tambah Buku
            </button>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default SchoolsPage
